import { FC } from 'react';
import {
    AppIcon,
    Select,
    Theme,
    AppText,
    ReactSelectComponents,
    Box,
    Row,
    ExtractProps,
    ToastTypes,
} from '@streem/ui-react';
import {
    getPreferredAudioInputId,
    setPreferredAudioInputId,
    getPreferredAudioOutputId,
    setPreferredAudioInput,
    setPreferredAudioOutput,
} from '@streem/toolbox';
import { recordElementClicked } from '@streem/analytics';
import { DeviceSettingsWrapper } from './shared_components';
import { useAudioDevices } from '../../hooks/use_devices';
import { DeviceListItem, useDeviceList } from '../../hooks/use_device_list';
import { useGlobalStore } from '../../hooks/use_global_context';

interface AudioDropdownSelectorProps {
    onOpenDeviceSettings: () => void;
}

const audioDropdownOpenedEvent = 'nav-audio-select-opened';
const audioOptionSelectedEvent = (kind: MediaDeviceKind) => `nav-audio-${kind}-item-selected`;
const mediaSettingsOpenedId = 'nav-media-settings-opened';

export const AudioDropdownSelector: FC<React.PropsWithChildren<AudioDropdownSelectorProps>> = ({
    onOpenDeviceSettings,
}) => {
    const { audioInputDevices, audioOutputDevices, hasAudioPermission } = useAudioDevices();
    const { uiStore } = useGlobalStore();
    const { deviceList: audioInputDeviceList } = useDeviceList(
        audioInputDevices,
        getPreferredAudioInputId,
        setPreferredAudioInputId,
    );
    const { deviceList: audioOutputDeviceList } = useDeviceList(
        audioOutputDevices,
        getPreferredAudioOutputId,
        setPreferredAudioOutput,
    );

    const handleOpenDeviceSettings = () => {
        onOpenDeviceSettings();
        recordElementClicked(mediaSettingsOpenedId);
    };

    const onMenuOpen = () => {
        recordElementClicked(audioDropdownOpenedEvent);
    };

    const groupedOptions = [
        {
            label: 'Select a Microphone',
            options: audioInputDeviceList,
        },
        {
            label: 'Select a Speaker',
            options: audioOutputDeviceList,
        },
    ];

    const deviceChangeAlert = (inputOrOutput: MediaDeviceKind, label: string) => {
        const type = inputOrOutput === 'audioinput' ? 'input' : 'output';
        const deviceType = inputOrOutput === 'audioinput' ? 'Microphone' : 'Speaker';
        uiStore.removeToast(`audio-${type}-changed`);
        uiStore.addToast({
            content: `${deviceType} changed to ${label}`,
            flavor: ToastTypes.INFO,
            id: `audio-${type}-changed`,
            timeout: 3000,
        });
    };
    const handleSelect = (option: unknown) => {
        const device = option as DeviceListItem;
        if (device.kind === 'audioinput') {
            setPreferredAudioInput(device.deviceId, device.label);
            recordElementClicked(audioOptionSelectedEvent(device.kind));
        }
        if (device.kind === 'audiooutput') {
            setPreferredAudioOutput(device.deviceId, device.label);
            recordElementClicked(audioOptionSelectedEvent(device.kind));
        }
        deviceChangeAlert(device.kind, device.label);
    };

    return (
        <Box mr="1.5rem">
            <Select
                id="nav-audio-dropdown-select"
                isSearchable={false}
                onMenuOpen={onMenuOpen}
                onSelect={handleSelect}
                options={groupedOptions}
                formatOptionLabel={FormatOptionLabel}
                components={{
                    NoOptionsMessage: props => {
                        return (
                            <>
                                <Box p={'0.5rem 2rem 0.5rem 1rem'} mt={'.25em'}>
                                    <AppText
                                        size="medium"
                                        bold
                                        style={{
                                            fontWeight: '700',
                                        }}
                                    >
                                        Select a Microphone
                                    </AppText>
                                </Box>
                                <ReactSelectComponents.NoOptionsMessage {...props}>
                                    <AppText color="grey40" size="medium">
                                        No Options Available
                                    </AppText>
                                </ReactSelectComponents.NoOptionsMessage>

                                <Box
                                    p={'0.5rem 2rem 0.5rem 1rem'}
                                    mt={'.25em'}
                                    borderTop={`solid 1px ${Theme.colors.grey10}`}
                                >
                                    <AppText
                                        size="medium"
                                        bold
                                        style={{
                                            fontWeight: '700',
                                        }}
                                    >
                                        Select a Speaker
                                    </AppText>
                                </Box>

                                <ReactSelectComponents.NoOptionsMessage {...props}>
                                    <AppText color="grey40" size="medium">
                                        No Options Available
                                    </AppText>
                                </ReactSelectComponents.NoOptionsMessage>
                            </>
                        );
                    },
                    ClearIndicator: () => null,
                    IndicatorSeparator: () => null,
                    Placeholder: props => {
                        const iconName = hasAudioPermission ? 'MuteIcon' : 'MutedIcon';
                        const iconColor = hasAudioPermission
                            ? Theme.colors.black
                            : Theme.colors.red50;
                        return (
                            <ReactSelectComponents.Placeholder {...props}>
                                <AppIcon name={iconName} color={iconColor} slashcolor={iconColor} />
                            </ReactSelectComponents.Placeholder>
                        );
                    },
                    SingleValue,
                    MenuList: props => {
                        return (
                            <ReactSelectComponents.MenuList {...props}>
                                <div>{props.children}</div>

                                <DeviceSettingsWrapper
                                    p={'0.75rem 5.4rem 0.75rem 1rem'}
                                    borderTop={`0.065rem ${Theme.colors.grey10} solid`}
                                    onClick={handleOpenDeviceSettings}
                                >
                                    <AppText size="medium" bold fontWeight={700}>
                                        Device Settings
                                    </AppText>
                                </DeviceSettingsWrapper>
                            </ReactSelectComponents.MenuList>
                        );
                    },
                }}
                styles={{
                    group: base => ({
                        ...base,
                        'div:first-of-type': {
                            border: 'none',
                        },
                        borderTop: `solid 1px ${Theme.colors.grey10}`,
                    }),
                    groupHeading: base => ({
                        ...base,
                        fontSize: '1rem',
                        color: Theme.colors.grey90,
                        fontWeight: 700,
                        textWrap: 'wrap',
                        padding: '0.5rem 2rem 0.5rem 1rem',
                        textTransform: 'none',
                        fontFamily: Theme.text.bodyFontFamily,
                    }),
                    noOptionsMessage: base => ({
                        ...base,
                        textAlign: 'center',
                    }),
                    singleValue: base => ({
                        ...base,
                        marginLeft: '0px',
                        overflow: 'unset',
                    }),
                    placeholder: base => ({
                        ...base,
                        marginLeft: '0px',
                    }),
                    control: (base, { selectProps }) => ({
                        ...base,
                        width: '68px',
                        height: '2.5rem',
                        borderRadius: '2rem',
                        border: 'none',
                        cursor: 'pointer',
                        background: selectProps.isDisabled ? Theme.colors.grey10 : undefined,
                        ':hover': {
                            background: Theme.colors.blue05,
                        },
                    }),
                    menu: base => ({
                        ...base,
                        marginTop: '1.1rem',
                        borderRadius: 4,
                        right: 0,
                        width: '13.25rem',
                    }),
                    menuList: base => ({
                        ...base,
                        padding: 0,
                    }),
                    option: base => ({
                        ...base,
                        borderRadius: '0.25rem',
                        overflow: 'hidden',
                        backgroundColor: 'none',
                        ':hover': { backgroundColor: Theme.colors.blue05, cursor: 'pointer' },
                    }),
                    dropdownIndicator: (base, { selectProps }) => ({
                        ...base,
                        transform: selectProps.menuIsOpen ? 'rotate(180deg)' : null,
                        transition: 'all .2s ease',
                        color: Theme.colors.grey50,
                        ':hover': {
                            color: Theme.colors.grey50,
                        },
                    }),
                }}
            />
        </Box>
    );
};

type FormatOptionLabel = ExtractProps<typeof Select>['formatOptionLabel'];
const FormatOptionLabel: FormatOptionLabel = data => {
    const option = data as DeviceListItem;
    return <OptionLabel option={option} />;
};

const SingleValue: typeof ReactSelectComponents.SingleValue = props => {
    return (
        <ReactSelectComponents.SingleValue {...props}>
            <AppIcon name="MuteIcon" color={Theme.colors.black} />
        </ReactSelectComponents.SingleValue>
    );
};

const OptionLabel = ({ option }: { option: DeviceListItem }) => {
    const isSelectedOption =
        (option.deviceId === getPreferredAudioInputId() && option.kind === 'audioinput') ||
        (option.deviceId === getPreferredAudioOutputId() && option.kind === 'audiooutput');
    return (
        <Row alignItems="center" id="row" style={{ gap: '.56rem' }}>
            {isSelectedOption && <AppIcon name="DoneIcon" color={Theme.colors.black} />}
            {/* Hack to keep left margin between text */}
            {!isSelectedOption && <span style={{ width: '1.5rem', height: '1.5rem' }} />}
            <AppText as="span">{option.displayLabel}</AppText>
        </Row>
    );
};
